import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import {
  type ConnectionRegistration,
  ConnectionCredential,
  ConnectionProfile,
} from "../connection/catalog.ts";
import { type ConnectionTarget, PersistedConnectionTarget } from "../connection/model.ts";
import * as TokenStore from "../authorization/tokenStore.ts";

export const StoredConnectionCredential = Schema.Struct({
  connectionId: Schema.String,
  credential: ConnectionCredential,
});
export type StoredConnectionCredential = typeof StoredConnectionCredential.Type;

export const ConnectionCatalogDocument = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  targets: Schema.Array(PersistedConnectionTarget),
  profiles: Schema.Array(ConnectionProfile),
  credentials: Schema.Array(StoredConnectionCredential),
  remoteDpopTokens: Schema.Array(TokenStore.RemoteDpopAccessToken),
});
export type ConnectionCatalogDocument = typeof ConnectionCatalogDocument.Type;

export const EMPTY_CONNECTION_CATALOG_DOCUMENT: ConnectionCatalogDocument = Object.freeze({
  schemaVersion: 1,
  targets: [],
  profiles: [],
  credentials: [],
  remoteDpopTokens: [],
});

const CONNECTION_CATALOG_COLLECTIONS = [
  "targets",
  "profiles",
  "credentials",
  "remoteDpopTokens",
] as const;
type ConnectionCatalogCollection = (typeof CONNECTION_CATALOG_COLLECTIONS)[number];

/** How many stored records of each collection a salvaged read left out. Never the records. */
export type ConnectionCatalogRejectedCounts = {
  readonly [Collection in ConnectionCatalogCollection]: number;
};

export type ConnectionCatalogDocumentReadResult =
  | { readonly kind: "ok"; readonly doc: ConnectionCatalogDocument }
  | {
      readonly kind: "salvaged";
      readonly doc: ConnectionCatalogDocument;
      readonly rejected: ConnectionCatalogRejectedCounts;
    }
  | { readonly kind: "future-version"; readonly version: number }
  | { readonly kind: "unparseable" };

const decodeDocument = Schema.decodeUnknownOption(ConnectionCatalogDocument);
const decodeTarget = Schema.decodeUnknownOption(PersistedConnectionTarget);
const decodeProfile = Schema.decodeUnknownOption(ConnectionProfile);
const decodeCredential = Schema.decodeUnknownOption(StoredConnectionCredential);
const decodeRemoteDpopToken = Schema.decodeUnknownOption(TokenStore.RemoteDpopAccessToken);

function decodeEach<A>(
  value: unknown,
  decode: (input: unknown) => Option.Option<A>,
): { readonly values: ReadonlyArray<A>; readonly rejected: number } {
  if (value === undefined) {
    return { values: [], rejected: 0 };
  }
  if (!Array.isArray(value)) {
    return { values: [], rejected: 1 };
  }
  const values: Array<A> = [];
  let rejected = 0;
  for (const record of value) {
    const decoded = decode(record);
    if (Option.isSome(decoded)) {
      values.push(decoded.value);
    } else {
      rejected += 1;
    }
  }
  return { values, rejected };
}

function requiredProfileTag(target: PersistedConnectionTarget): ConnectionProfile["_tag"] | null {
  switch (target._tag) {
    case "RelayConnectionTarget":
      return null;
    case "BearerConnectionTarget":
      return "BearerConnectionProfile";
    case "SshConnectionTarget":
      return "SshConnectionProfile";
  }
}

/**
 * Reads a stored connection catalog without letting one bad record cost the
 * others. A well-formed version 1 document is returned as stored. Otherwise
 * each record is decoded on its own; a connection whose target, required
 * profile or bearer credential was rejected is left out whole, as are
 * profiles and credentials left without a target. A newer schema version is
 * reported without being read, so the caller can leave it untouched.
 */
export function readConnectionCatalogDocument(raw: string): ConnectionCatalogDocumentReadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { kind: "unparseable" };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { kind: "unparseable" };
  }
  const stored = parsed as Readonly<Record<string, unknown>>;
  const version = stored.schemaVersion;
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) {
    return { kind: "unparseable" };
  }
  if (version > 1) {
    return { kind: "future-version", version };
  }

  const whole = decodeDocument(stored);
  if (Option.isSome(whole)) {
    return { kind: "ok", doc: whole.value };
  }

  const targets = decodeEach(stored.targets, decodeTarget);
  const profiles = decodeEach(stored.profiles, decodeProfile);
  const credentials = decodeEach(stored.credentials, decodeCredential);
  const remoteDpopTokens = decodeEach(stored.remoteDpopTokens, decodeRemoteDpopToken);

  const profileTags = new Map(
    profiles.values.map((profile) => [profile.connectionId, profile._tag] as const),
  );
  const credentialIds = new Set(credentials.values.map((entry) => entry.connectionId));
  const keptTargets = targets.values.filter((target) => {
    const profileTag = requiredProfileTag(target);
    if (profileTag === null || !("connectionId" in target)) {
      return true;
    }
    if (profileTags.get(target.connectionId) !== profileTag) {
      return false;
    }
    return target._tag !== "BearerConnectionTarget" || credentialIds.has(target.connectionId);
  });
  const keptConnectionIds = new Set(
    keptTargets.flatMap((target) => ("connectionId" in target ? [target.connectionId] : [])),
  );
  const keptProfiles = profiles.values.filter((profile) =>
    keptConnectionIds.has(profile.connectionId),
  );
  const keptCredentials = credentials.values.filter((entry) =>
    keptConnectionIds.has(entry.connectionId),
  );

  return {
    kind: "salvaged",
    doc: {
      schemaVersion: 1,
      targets: keptTargets,
      profiles: keptProfiles,
      credentials: keptCredentials,
      remoteDpopTokens: remoteDpopTokens.values,
    },
    rejected: {
      targets: targets.rejected + (targets.values.length - keptTargets.length),
      profiles: profiles.rejected + (profiles.values.length - keptProfiles.length),
      credentials: credentials.rejected + (credentials.values.length - keptCredentials.length),
      remoteDpopTokens: remoteDpopTokens.rejected,
    },
  };
}

export function replaceCatalogValue<A>(
  values: ReadonlyArray<A>,
  key: (value: A) => string,
  next: A,
): ReadonlyArray<A> {
  const nextKey = key(next);
  return [...values.filter((value) => key(value) !== nextKey), next];
}

export function removeCatalogValue<A>(
  values: ReadonlyArray<A>,
  key: (value: A) => string,
  removedKey: string,
): ReadonlyArray<A> {
  return values.filter((value) => key(value) !== removedKey);
}

function connectionIdOf(target: ConnectionTarget): string | null {
  switch (target._tag) {
    case "PrimaryConnectionTarget":
    case "RelayConnectionTarget":
      return null;
    case "BearerConnectionTarget":
    case "SshConnectionTarget":
      return target.connectionId;
  }
}

function removeConnectionMetadata(
  document: ConnectionCatalogDocument,
  target: ConnectionTarget,
  removeRemoteToken: boolean,
): ConnectionCatalogDocument {
  const connectionId = connectionIdOf(target);
  return {
    ...document,
    targets: removeCatalogValue(
      document.targets,
      (value) => value.environmentId,
      target.environmentId,
    ),
    profiles:
      connectionId === null
        ? document.profiles
        : removeCatalogValue(document.profiles, (value) => value.connectionId, connectionId),
    credentials:
      connectionId === null
        ? document.credentials
        : removeCatalogValue(document.credentials, (value) => value.connectionId, connectionId),
    remoteDpopTokens: removeRemoteToken
      ? removeCatalogValue(
          document.remoteDpopTokens,
          (value) => value.environmentId,
          target.environmentId,
        )
      : document.remoteDpopTokens,
  };
}

export function registerConnectionInCatalog(
  document: ConnectionCatalogDocument,
  registration: ConnectionRegistration,
): ConnectionCatalogDocument {
  const target = registration.target;
  const previous = document.targets.find(
    (candidate) => candidate.environmentId === target.environmentId,
  );
  const cleaned =
    previous === undefined ? document : removeConnectionMetadata(document, previous, false);
  const next: ConnectionCatalogDocument = {
    ...cleaned,
    targets: replaceCatalogValue(cleaned.targets, (value) => value.environmentId, target),
  };

  switch (registration._tag) {
    case "RelayConnectionRegistration":
      return next;
    case "BearerConnectionRegistration":
      return {
        ...next,
        profiles: replaceCatalogValue(
          next.profiles,
          (value) => value.connectionId,
          registration.profile,
        ),
        credentials: replaceCatalogValue(next.credentials, (value) => value.connectionId, {
          connectionId: registration.target.connectionId,
          credential: registration.credential,
        }),
      };
    case "SshConnectionRegistration":
      return {
        ...next,
        profiles: replaceCatalogValue(
          next.profiles,
          (value) => value.connectionId,
          registration.profile,
        ),
      };
  }
}

export function removeConnectionFromCatalog(
  document: ConnectionCatalogDocument,
  target: ConnectionTarget,
): ConnectionCatalogDocument {
  return removeConnectionMetadata(document, target, true);
}
